/** @format */
import { Divider } from '@mpb-com/feather/components/atoms/divider';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Skeleton,
    SkeletonVariant,
} from '@mpb-com/feather/components/atoms/skeleton';
import { Trans, useTranslation } from 'next-i18next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import qs from 'qs';
import React, { FunctionComponent, ReactNode, useContext, useRef } from 'react';
import styled from 'styled-components';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink, getLocalizedPath } from '@toucan/client/global/nextjs';

import { scrollToElement } from '@toucan/client/core/scroll';

import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';
import { Link } from '@toucan/client/shared/components/link';
import {
    Pagination,
    PaginationItemProps,
} from '@toucan/client/shared/components/pagination';

import { ButtonDivider } from '@toucan/client/blocks/components/button-divider';

import { SortBySelect } from '@toucan/client/features/search/components/sort-by-select';
import { SortByOption } from '@toucan/client/features/search/config/types';

import { getSearchResultsGridColumns } from './helpers';
import { styles } from './search-results.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum PaginationVariant {
    PAGE_SELECT = 'page_select',
    VIEW_MORE = 'view_more',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum SearchResultsVariant {
    MODEL = 'model',
    PRODUCT = 'product',
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type SearchResultsProps = {
    isLoading?: boolean;
    'data-testid'?: string;
    className?: string;
    paginationVariant?: PaginationVariant;
    variant: SearchResultsVariant;
    resultsInView: ReactNode[];
    resultsInViewCount?: number;
    /**
     * The total number of results
     * NOTE: -1 signifies that the total number of results is currently unknown
     */
    totalResultsCount: number;
    resultsPerPage?: number;
    numResultSkeletons?: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    displayAllResults?: boolean;
    page: number;
    sortBy: SortByOption;
    sortByOptions: SortByOption[];
    onPaginationClick: (page: number) => void;
    onSeeAllClick: () => void;
    onCreateAlert?: () => void;
    onSortByChange: (sortByOption: SortByOption) => void;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    displayOutOfStock?: boolean;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    noInStockResults?: boolean;
    onFindOutOfStockModels?: () => void;
    onBackToInStockModels?: () => void;
};

type StyledSearchResultsProps = Omit<
    SearchResultsProps,
    'onPaginationClick' | 'onSeeAllClick' | 'onSortByChange'
>;

const StyledSearchResults = styled.div<StyledSearchResultsProps>`
    ${styles}
`;

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
enum PaginationControlsPosition {
    TOP = 'top',
    BOTTOM = 'bottom',
}

/**
 * Represents the type of message that should be displayed at the end of search results
 */
// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
enum SearchMessageType {
    /**
     * Represents the message that is displayed when the user has tried to navigate out of bounds
     */
    OUT_OF_BOUNDS,

    /**
     * Represents the message that is displayed when the user has reached the end of results on a model listings page.
     */
    END_OF_RESULTS_MODEL,

    /**
     * Represents the message that is displayed when the user has reached the end of results on a model listings page.
     */
    END_OF_RESULTS_PRODUCT,

    /**
     * Represents the message to be displayed when there are no results.
     */
    NO_RESULTS,
}

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
    isLoading = false,
    paginationVariant = PaginationVariant.PAGE_SELECT,
    resultsInView,
    resultsInViewCount,
    resultsPerPage = 52,
    numResultSkeletons = 0,
    displayAllResults = false,
    totalResultsCount,
    page,
    sortBy,
    sortByOptions,
    onPaginationClick,
    onSeeAllClick,
    onSortByChange,
    variant,
    onCreateAlert,
    displayOutOfStock = false,
    noInStockResults = false,
    onFindOutOfStockModels,
    onBackToInStockModels,
    ...otherProps
}: SearchResultsProps) => {
    const { t } = useTranslation();

    const isMobile = useMediaQuery(MediaQuery.MOBILE);
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);

    const { activeMarket } = useContext(MarketContext);
    const router = useRouter();

    const topOfResults = useRef<HTMLDivElement | null>(null);

    const totalPages =
        totalResultsCount !== -1
            ? Math.ceil(totalResultsCount / resultsPerPage)
            : -1;

    const paginationItemsLimit = isMobile ? 5 : 7;
    const resultsInViewsLength =
        (resultsInViewCount ?? resultsInView.length) - numResultSkeletons;

    const isOutOfBounds = page >= totalPages;
    const isLoadingResults = totalResultsCount === -1;

    const hasResults = totalResultsCount > 0;

    // isOutOfBounds is true when we have no results, and we want to index the first page in this case
    const shouldNotIndex =
        (hasResults && isOutOfBounds) || (!hasResults && page > 0);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayShowAllControl =
        !displayAllResults && totalResultsCount > resultsInView.length;

    /**
     * Return a SearchMessageType to control the message (if any) that is displayed at the bottom of
     * the search listing page. Returns null when no message should be displayed
     *
     * @returns {SearchMessageType | null}
     */
    function getSearchMessageType(): SearchMessageType | null {
        if (isLoadingResults) {
            return null;
        }

        const isLastPage = displayAllResults
            ? totalResultsCount === resultsInView.length
            : page + 1 >= totalPages;

        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        const noResults = totalPages < 1 && resultsInViewsLength === 0;

        if (noResults) {
            return SearchMessageType.NO_RESULTS;
        }

        if (isOutOfBounds) {
            return SearchMessageType.OUT_OF_BOUNDS;
        }

        if (isLastPage || displayAllResults) {
            if (variant === SearchResultsVariant.PRODUCT) {
                return SearchMessageType.END_OF_RESULTS_PRODUCT;
            }
            return SearchMessageType.END_OF_RESULTS_MODEL;
        }

        return null;
    }

    const searchMessageType = getSearchMessageType();

    /**
     * Return the page href for a particular pagination link, removing the page param if
     * the page is 1. This allows the generated URLs to be correct in markup.
     *
     * @param {number} pageNumber The zero indexed page
     * @return {string} The href to add to the pagination link
     */
    function getPageHref(pageNumber: number): string {
        const newPageNumber = pageNumber + 1;

        const params = qs.parse(router.asPath.split('?')[1] ?? '');

        const preparedParams = qs.stringify(
            {
                ...params,
                page: newPageNumber > 1 ? newPageNumber : undefined,
            },
            { addQueryPrefix: true }
        );

        return `${getLocalizedPath(router)}${preparedParams}`;
    }

    const paginationItems: PaginationItemProps[] = [];
    for (let i = 0; i < totalPages; i++) {
        paginationItems.push({
            text: `${i + 1}`,
            href: getPageHref(i),
        });
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displaySeeMoreButton =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        page + 1 < totalPages &&
        !displayAllResults;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displaySeeLessButton =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        page + 1 >= totalPages &&
        page !== 0 &&
        !displayAllResults;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayContentLine =
        paginationVariant === PaginationVariant.VIEW_MORE &&
        !displaySeeMoreButton &&
        !displaySeeLessButton;

    /**
     * Get the total results as a ReactNode
     *
     * @returns {ReactNode} A react node representing the total results
     */
    function renderTotalResults() {
        if (isLoading || totalResultsCount === -1) {
            return (
                <Skeleton
                    data-testid="search-results__results-skeleton"
                    className="total-results-skeleton"
                    variant={SkeletonVariant.TEXT}
                />
            );
        }

        if (isSmallTablet) {
            return (
                <span data-testid="search-results__mobile-result-count">
                    {t('{{totalResultsCount}} results', {
                        totalResultsCount: totalResultsCount.toString(),
                    })}
                </span>
            );
        }

        return (
            <span data-testid="search-results__desktop-result-count">
                {t(
                    'Showing {{displayedResults}} of {{totalResultsCount}} results',
                    {
                        displayedResults: displayAllResults
                            ? totalResultsCount.toString()
                            : resultsInViewsLength.toString(),
                        totalResultsCount: totalResultsCount.toString(),
                    }
                )}
            </span>
        );
    }

    /**
     * Get the pagination controls as a ReactNode
     *
     * @returns {ReactNode} - A react node representing the pagination controls
     */
    function renderPaginationControls(
        position: PaginationControlsPosition
    ): ReactNode {
        const isPageSelectVariant =
            paginationVariant === PaginationVariant.PAGE_SELECT;

        const isBottomPosition = position === PaginationControlsPosition.BOTTOM;

        const shouldRenderPagination =
            !displayAllResults && isPageSelectVariant && isBottomPosition;

        const shouldRenderTotalResults = isPageSelectVariant // eslint-disable-line no-nested-ternary -- Legacy code: do not use as an example.
            ? isMobile
                ? !isBottomPosition
                : true
            : true;

        return (
            <div className="pagination">
                <div
                    className="results-meta"
                    data-testid="search-results__meta"
                >
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {shouldRenderTotalResults && renderTotalResults()}

                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {displayShowAllControl && (
                        <Link
                            onClick={onSeeAllClick}
                            data-testid="search-results__see-all"
                        >
                            {t('See all')}
                        </Link>
                    )}
                </div>
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {shouldRenderPagination && (
                    <Pagination
                        currentItem={page}
                        items={paginationItems}
                        itemLimit={paginationItemsLimit}
                        showFirstLastButtons={true}
                        showPrevNextButtons={true}
                        itemLabel={(item) =>
                            t('Page {{pageNumber}}', {
                                pageNumber: item + 1,
                            })
                        }
                        onPage={onPaginationClick}
                        data-testid="search-results__pagination"
                    />
                )}
            </div>
        );
    }

    /**
     *
     * Get a message to display in the search results as a ReactNode
     * @param {ReactNode} title - A title for the results message
     * @param {ReactNode} subtitle - A subtitle for the results message
     * @param {ReactNode} [link] - An optional link to be displayed. When nothing is passed a default link is displayed
     *
     * @returns {ReactNode} - A react node representing a search results message
     */
    function getSearchResultsMessage(
        title: ReactNode,
        subtitle: ReactNode,
        link?: ReactNode
    ): ReactNode {
        link =
            link ??
            (variant === SearchResultsVariant.MODEL &&
                !noInStockResults &&
                (displayOutOfStock ? (
                    <Link
                        onClick={onBackToInStockModels}
                        className="back-to-in-stock-models-button"
                        data-testid="search-results__back-to-in-stock-models-button"
                    >
                        {t('Go back to models in stock')}
                    </Link>
                ) : (
                    <Link
                        onClick={onFindOutOfStockModels}
                        className="find-out-of-stock-models-button"
                        data-testid="search-results__find-out-of-stock-models-button"
                    >
                        {t('Find out of stock models')}
                    </Link>
                )));

        return (
            <div
                className="results-message"
                data-testid="search-results__results-message"
            >
                <Heading size={HeadingSize.H4} as={'div'} className="title">
                    {title}
                </Heading>
                {subtitle !== null && <p className="subtitle">{subtitle}</p>}
                {link}
            </div>
        );
    }

    /**
     * Conditionally returns a message to be shown in the search results.
     *
     * @returns {ReactNode | null} The message to display as a ReactNode or null
     * when there is nothing to display
     */
    function getSearchMessage(): ReactNode {
        switch (searchMessageType) {
            case SearchMessageType.OUT_OF_BOUNDS:
                return getSearchResultsMessage(
                    t("You've gone too far!"),
                    t("We don't have this many results to show you")
                );

            case SearchMessageType.END_OF_RESULTS_PRODUCT:
                return getSearchResultsMessage(
                    t("That's all of our available options"),
                    onCreateAlert ? (
                        <Trans
                            components={[
                                <Link key={0} onClick={onCreateAlert}>
                                    $0
                                </Link>,
                            ]}
                        >
                            {t(
                                'If none of these are suitable, <0>create an alert</0>. We add more products every day.'
                            )}
                        </Trans>
                    ) : null
                );
            case SearchMessageType.END_OF_RESULTS_MODEL:
                return getSearchResultsMessage(
                    t("That's all of our results"),
                    t(
                        "If you didn't find what you were looking for, try changing your search criteria."
                    )
                );
            case SearchMessageType.NO_RESULTS:
                return getSearchResultsMessage(
                    t('We couldn’t find any results.'),
                    t('Try changing your search criteria.'),
                    <div
                        className="no-results-message"
                        data-testid="search-results__no-results"
                    >
                        <Trans
                            components={[
                                <Link key={0} onClick={onFindOutOfStockModels}>
                                    $0
                                </Link>,
                                <WrappedLink
                                    key={1}
                                    href="/search?q=*"
                                    as={`/${activeMarket.slug}/${t('search', {
                                        ns: 'urls',
                                    })}?q=*`}
                                    className="show-all-models-link"
                                    passHref={true}
                                >
                                    $1
                                </WrappedLink>,
                            ]}
                        >
                            {variant === SearchResultsVariant.MODEL &&
                            !noInStockResults &&
                            !displayOutOfStock
                                ? t(
                                      'Alternatively, <0>find out of stock models</0> or <1>why not explore all of our camera gear</1>'
                                  )
                                : t(
                                      '<1>Why not explore all of our camera gear</1>'
                                  )}
                        </Trans>
                    </div>
                );
            default:
                return null;
        }
    }

    return (
        <StyledSearchResults
            paginationVariant={paginationVariant}
            resultsInView={resultsInView}
            resultsPerPage={resultsPerPage}
            totalResultsCount={totalResultsCount}
            page={page}
            sortBy={sortBy}
            variant={variant}
            sortByOptions={sortByOptions}
            {...otherProps}
        >
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {shouldNotIndex && (
                <Head>
                    <meta name="robots" content="noindex" />
                </Head>
            )}
            <ConstrainedWrapper
                as="section"
                className="controls"
                data-testid="search-results__controls"
                type={ConstrainedWrapperType.PADDING}
            >
                {renderPaginationControls(PaginationControlsPosition.TOP)}

                <div className="sorting">
                    <SortBySelect
                        options={sortByOptions}
                        selectedOption={sortBy}
                        onSortByChange={(sortByOption: SortByOption) => {
                            onSortByChange(sortByOption);
                        }}
                        data-testid="search-results__sort-by"
                    />
                </div>
            </ConstrainedWrapper>

            <ConstrainedWrapper as="section" className="results">
                <div ref={topOfResults} />
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {displayOutOfStock &&
                    !noInStockResults &&
                    variant === SearchResultsVariant.MODEL && (
                        <div className="back-to-in-stock-models">
                            {t('Showing out of stock models only.')}{' '}
                            <Link
                                onClick={onBackToInStockModels}
                                className="back-to-in-stock-models-button"
                            >
                                {t('Go back to models in stock')}
                            </Link>
                        </div>
                    )}

                <GridRepeater
                    data-testid="search-results__grid"
                    gridGap="var(--layout-spacing-12)"
                    className="search-results-grid"
                    gridColumns={getSearchResultsGridColumns()}
                >
                    {resultsInView.map((item: ReactNode) => item)}
                </GridRepeater>
            </ConstrainedWrapper>

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displaySeeMoreButton && (
                <ButtonDivider
                    className="show-more"
                    onClick={() => {
                        onPaginationClick(page + 1);
                    }}
                    data-testid="search-results__see-more-button"
                >
                    {t('See more', {
                        context: 'Product listing',
                    })}
                </ButtonDivider>
            )}

            <ConstrainedWrapper
                as="section"
                className="pagination-bottom"
                type={ConstrainedWrapperType.PADDING}
            >
                {!isLoading && getSearchMessage()}
                {paginationVariant === PaginationVariant.PAGE_SELECT &&
                    !displayAllResults &&
                    totalPages > 1 &&
                    renderPaginationControls(PaginationControlsPosition.BOTTOM)}
            </ConstrainedWrapper>

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displaySeeLessButton && (
                <ButtonDivider
                    className="see-less"
                    data-testid="search-results__see-less-button-divider"
                    onClick={() => {
                        onPaginationClick(0);
                        scrollToElement(topOfResults.current);
                    }}
                >
                    {t('See less', {
                        context: 'Product listing',
                    })}
                </ButtonDivider>
            )}

            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displayContentLine && <Divider className="content-line" />}
        </StyledSearchResults>
    );
};
